import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, Button, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Tooltip, Snackbar, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Chip, Stack, Switch, Alert, CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import api from "../services/api";

/* The full QuickBooks account-type list (the "Choose one account type" screen).
 * Grouped exactly like QuickBooks so it feels familiar. */
const TYPE_GROUPS = [
  { group: "Income & Expense", types: ["Income", "Other Income", "Cost of Goods Sold", "Expense", "Other Expense"] },
  { group: "Assets", types: ["Bank", "Accounts Receivable", "Other Current Asset", "Fixed Asset", "Other Asset"] },
  { group: "Liabilities & Equity", types: ["Accounts Payable", "Credit Card", "Other Current Liability", "Long Term Liability", "Loan", "Equity"] },
];

const ROLLUP = {
  "Bank": "Asset", "Accounts Receivable": "Asset", "Other Current Asset": "Asset",
  "Fixed Asset": "Asset", "Other Asset": "Asset",
  "Accounts Payable": "Liability", "Credit Card": "Liability", "Other Current Liability": "Liability",
  "Long Term Liability": "Liability", "Loan": "Liability",
  "Equity": "Equity",
  "Income": "Income", "Other Income": "Income",
  "Cost of Goods Sold": "Expense", "Expense": "Expense", "Other Expense": "Expense",
};

const ROLLUP_ORDER = ["Asset", "Liability", "Equity", "Income", "Expense"];
const ROLLUP_COLOR = {
  Asset: "success", Liability: "warning", Equity: "secondary", Income: "primary", Expense: "default",
};
const STATEMENT = {
  Income: "Profit & Loss", Expense: "Profit & Loss",
  Asset: "Balance Sheet", Liability: "Balance Sheet", Equity: "Balance Sheet",
};

const EMPTY_FORM = { Account_ID: null, AccountNumber: "", AccountName: "", SubType: "Bank", ParentName: "", IsActive: true };

export default function ChartOfAccounts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [dialog, setDialog] = useState(null); // form object when open
  const [importing, setImporting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/accounting/chart")
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => setMsg("Failed to load chart of accounts"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const importQb = () => {
    setImporting(true);
    api.post("/accounting/chart/import")
      .then((r) => { setMsg(r.data?.message || "Imported"); load(); })
      .catch((e) => setMsg(e.response?.data?.error || "Import failed"))
      .finally(() => setImporting(false));
  };

  const save = () => {
    const f = dialog;
    if (!f.AccountName.trim()) { setMsg("Account name is required"); return; }
    const body = {
      AccountNumber: f.AccountNumber || null,
      AccountName: f.AccountName.trim(),
      SubType: f.SubType,
      ParentName: f.ParentName || null,
      IsActive: f.IsActive,
    };
    const req = f.Account_ID
      ? api.put(`/accounting/chart/${f.Account_ID}`, body)
      : api.post("/accounting/chart", body);
    req.then(() => { setMsg(f.Account_ID ? "Account updated" : "Account created"); setDialog(null); load(); })
      .catch((e) => setMsg(e.response?.data?.error || "Save failed"));
  };

  const remove = (row) => {
    if (!window.confirm(`Delete account "${row.AccountName}"?`)) return;
    api.delete(`/accounting/chart/${row.Account_ID}`)
      .then(() => { setMsg("Account deleted"); load(); })
      .catch((e) => setMsg(e.response?.data?.error || "Delete failed"));
  };

  // Group rows by roll-up type for display.
  const grouped = ROLLUP_ORDER.map((type) => ({
    type,
    rows: rows.filter((r) => r.AccountType === type),
  })).filter((g) => g.rows.length);

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Chart of Accounts</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            The money categories behind your Profit &amp; Loss and Balance Sheet. Not your customers/suppliers — those stay under Accounts.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={importQb} disabled={importing}
            startIcon={importing ? <CircularProgress size={16} /> : null}>
            Import my QuickBooks chart
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ ...EMPTY_FORM })}>
            Add account
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Your chart of accounts is empty. Click <b>Import my QuickBooks chart</b> to load your accounts
            (banks, fixed assets, income, cost of goods sold, and all your expense accounts) exactly as they
            are in QuickBooks. Your customers/suppliers are <b>not</b> imported — they stay in Accounts and
            feed A/R and A/P automatically. Nothing here affects your existing data; you can edit or remove
            any account afterward.
          </Alert>
          <Button variant="contained" onClick={importQb} disabled={importing}>Import my QuickBooks chart</Button>
        </Paper>
      ) : (
        grouped.map((g) => (
          <Paper key={g.type} sx={{ mb: 2, overflow: "hidden" }}>
            <Box sx={{ px: 2, py: 1, bgcolor: "action.hover", display: "flex", alignItems: "center", gap: 1 }}>
              <Chip size="small" label={g.type} color={ROLLUP_COLOR[g.type]} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{g.type}</Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>· {STATEMENT[g.type]}</Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Typography variant="caption" sx={{ color: "text.secondary" }}>{g.rows.length} account(s)</Typography>
            </Box>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 90 }}>Number</TableCell>
                    <TableCell>Account name</TableCell>
                    <TableCell sx={{ width: 200 }}>Type</TableCell>
                    <TableCell sx={{ width: 80 }}>Active</TableCell>
                    <TableCell sx={{ width: 100 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {g.rows.map((r) => (
                    <TableRow key={r.Account_ID} hover>
                      <TableCell sx={{ fontVariantNumeric: "tabular-nums", color: "text.secondary" }}>{r.AccountNumber || "—"}</TableCell>
                      <TableCell sx={{ fontWeight: r.ParentName ? 400 : 500, pl: r.ParentName ? 4 : 2 }}>
                        {r.ParentName ? <Box component="span" sx={{ color: "text.disabled", mr: 0.5 }}>└</Box> : null}
                        {r.AccountName}
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>{r.SubType}</TableCell>
                      <TableCell>{r.IsActive ? "Yes" : "No"}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => setDialog({
                          Account_ID: r.Account_ID, AccountNumber: r.AccountNumber || "",
                          AccountName: r.AccountName, SubType: r.SubType, IsActive: !!r.IsActive,
                        })}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => remove(r)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        ))
      )}

      {/* Add / edit dialog */}
      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog?.Account_ID ? "Edit account" : "Add account"}</DialogTitle>
        <DialogContent>
          {dialog && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Account number (optional)" size="small" value={dialog.AccountNumber}
                onChange={(e) => setDialog({ ...dialog, AccountNumber: e.target.value })} placeholder="e.g. 4000" />
              <TextField label="Account name" size="small" required value={dialog.AccountName}
                onChange={(e) => setDialog({ ...dialog, AccountName: e.target.value })} placeholder="e.g. Sales - Copper" />
              <TextField label="Account type" size="small" select value={dialog.SubType}
                onChange={(e) => setDialog({ ...dialog, SubType: e.target.value })}
                helperText={`Appears on the ${STATEMENT[ROLLUP[dialog.SubType]] || "statement"} (${ROLLUP[dialog.SubType] || "—"})`}>
                {TYPE_GROUPS.map((grp) => [
                  <MenuItem key={grp.group} disabled sx={{ opacity: 0.7, fontWeight: 700 }}>{grp.group}</MenuItem>,
                  ...grp.types.map((t) => (
                    <MenuItem key={t} value={t} sx={{ pl: 3 }}>{t}</MenuItem>
                  )),
                ])}
              </TextField>
              <TextField label="Parent account (optional)" size="small" select value={dialog.ParentName || ""}
                onChange={(e) => setDialog({ ...dialog, ParentName: e.target.value })}
                helperText="Nest this under another account (e.g. Supplies → Niton Gun)">
                <MenuItem value="">(none — top level)</MenuItem>
                {rows
                  .filter((a) => a.AccountType === ROLLUP[dialog.SubType] && !a.ParentName
                    && a.AccountName !== dialog.AccountName)
                  .map((a) => <MenuItem key={a.Account_ID} value={a.AccountName}>{a.AccountName}</MenuItem>)}
              </TextField>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch checked={dialog.IsActive} onChange={(e) => setDialog({ ...dialog, IsActive: e.target.checked })} />
                <Typography variant="body2">Active</Typography>
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={save}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!msg} autoHideDuration={3000} onClose={() => setMsg(null)} message={msg}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
    </Box>
  );
}
